import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api'; 
import { MapPin, X } from 'lucide-react';
import styles from './GoogleAddressAutocomplete.module.css';

const GoogleAddressAutocomplete = ({ value, onSelect, placeholder, disabled, onChange }) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const handleClear = (e) => {
        e.stopPropagation();
        setInputValue('');
        setSuggestions([]);
        setShowSuggestions(false);
        if (onChange) onChange('');
        if (onSelect) onSelect(null);
    };

    const handleInputChange = async (e) => {
        const val = e.target.value;
        setInputValue(val);
        
        if (onChange) {
            onChange(val); 
        }

        if (val.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        try {
            // Call the backend proxy endpoint for address autocomplete
            const response = await api.get(`/api/GooglePlaces/addresscomplete?input=${encodeURIComponent(val)}`);
            
            if (response.predictions) {
                setSuggestions(response.predictions);
                setShowSuggestions(true);
            } else {
                setSuggestions([]);
                setShowSuggestions(false);
            }
        } catch (error) {
            console.error("Address search error:", error);
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSelectSuggestion = async (item) => {
        const address = item.description;
        setInputValue(address);
        setShowSuggestions(false);

        // Fetch details to get postcode/suburb/state
        try {
            // We need a way to get details. GooglePlacesController has 'placedetails' or 'google-place-details'
            // Let's use 'google-place-details' if it returns address components, but wait, 
            // the existing 'google-place-details' endpoint seems tailored for businesses (returns website, rating etc).
            // Let's check 'placedetails' endpoint in GooglePlacesController.
            // It calls https://maps.googleapis.com/maps/api/place/details/json?place_id={placeId}&key={apiKey}
            // This returns full details including address_components.
            
            const detailsResponse = await api.get(`/api/GooglePlaces/placedetails?placeId=${item.place_id}`);
            const result = detailsResponse.result;

            if (result) {
                let postcode = '';
                let suburb = '';
                let state = '';
                
                if (result.address_components) {
                    result.address_components.forEach(comp => {
                        if (comp.types.includes('postal_code')) postcode = comp.long_name;
                        if (comp.types.includes('locality')) suburb = comp.long_name;
                        if (comp.types.includes('administrative_area_level_1')) state = comp.short_name;
                    });
                }

                if (onSelect) {
                    onSelect({
                        fullAddress: address,
                        postcode: postcode,
                        suburb: suburb,
                        state: state,
                        placeId: item.place_id,
                        lat: result.geometry?.location?.lat,
                        lng: result.geometry?.location?.lng
                    });
                }
            } else if (onSelect) {
                 // Fallback if details fail
                 onSelect({
                    fullAddress: address,
                    placeId: item.place_id
                });
            }

        } catch (error) {
            console.error("Error fetching place details:", error);
             if (onSelect) {
                 onSelect({
                    fullAddress: address,
                    placeId: item.place_id
                });
            }
        }
    };

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <div className={styles.inputContainer}>
                <MapPin className={styles.inputIcon} size={16} />
                <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    disabled={disabled}
                    placeholder={placeholder || "Start typing address..."}
                    className={styles.input}
                    autoComplete="off"
                />
                {inputValue && !disabled && (
                    <button type="button" className={styles.clearBtn} onClick={handleClear}>
                        <X size={16} />
                    </button>
                )}
            </div>
            
            {showSuggestions && suggestions.length > 0 && (
                <ul className={styles.suggestionsList}>
                    {suggestions.map((item) => (
                        <li key={item.place_id} onClick={() => handleSelectSuggestion(item)} className={styles.suggestionItem}>
                            <MapPin size={14} style={{marginRight: '12px', marginTop: '4px', color: '#64748B', flexShrink: 0}}/>
                            <div className={styles.suggestionText}>
                                <span className={styles.mainText}>
                                    {item.structured_formatting?.main_text || item.description}
                                </span>
                                <span className={styles.secondaryText}>
                                    {item.structured_formatting?.secondary_text || ''}
                                </span>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default GoogleAddressAutocomplete;